/**
 * `<RunSheetPanel/>` - paste a run sheet, check the rows, make the tasks (#2655).
 *
 * Three steps in one side drawer: paste (one duty per line, plus the day),
 * check (a row per line the lead can correct, warnings under the row), done
 * (a count and an Undo that archives the whole paste). Nothing is written
 * until "Make N tasks".
 */

import { useState } from "react";

import { SideDrawer } from "../../components/ui/SideDrawer";
import {
  type RunSheetDraftRow,
  toDraftRow,
  useArchiveManyTaskNodes,
  useCreateRunSheet,
  useRunSheetPreview,
} from "../api/runSheet";
import { useArchiveTaskNode } from "../api/taskNodes";
import type { TaskMember } from "../members/useTaskTeamMembers";

export interface RunSheetPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string | undefined;
  members: TaskMember[];
}

function todayLocal(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

const INPUT = "w-full rounded border border-border bg-surface px-2 py-1 text-sm text-fg";
const BUTTON = "rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-800";
const PRIMARY = "rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50";

export function RunSheetPanel({ open, onOpenChange, teamId, members }: RunSheetPanelProps) {
  const [text, setText] = useState("");
  const [date, setDate] = useState(todayLocal);
  const [rows, setRows] = useState<RunSheetDraftRow[] | null>(null);
  const [done, setDone] = useState<{
    parentId: string;
    parentReused: boolean;
    nodeIds: string[];
    count: number;
    skipped: number;
  } | null>(null);
  const [undone, setUndone] = useState(false);
  const preview = useRunSheetPreview(teamId);
  const create = useCreateRunSheet(teamId);
  const archive = useArchiveTaskNode(teamId);
  const archiveMany = useArchiveManyTaskNodes(teamId);
  const [undoFailed, setUndoFailed] = useState(false);

  const reset = () => {
    setText("");
    setRows(null);
    setDone(null);
    setUndone(false);
    setUndoFailed(false);
    preview.reset();
    create.reset();
  };

  const runPreview = () => {
    preview.mutate({ text }, { onSuccess: (res) => setRows(res.rows.map(toDraftRow)) });
  };

  const edit = (index: number, patch: Partial<RunSheetDraftRow>) =>
    setRows((current) =>
      (current ?? []).map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );

  // Rows with a duty name go to the server. Only rows with a start time are made.
  const usable = (rows ?? []).filter((r) => r.title.trim().length > 0);
  const timed = usable.filter((r) => Boolean(r.time));
  const untimed = usable.length - timed.length;

  const tooLong = (): string | null => {
    for (const [i, row] of (rows ?? []).entries()) {
      if (!row.title.trim()) continue;
      if (row.title.trim().length > 500)
        return `Row ${i + 1}: the duty name is too long. Use 500 characters or fewer.`;
      if ((row.place ?? "").trim().length > 200)
        return `Row ${i + 1}: the place is too long. Use 200 characters or fewer.`;
    }
    return null;
  };
  const rowError = tooLong();

  const make = () => {
    if (rowError) return;
    create.mutate(
      { date, rows: usable },
      {
        onSuccess: (res) =>
          setDone({
            parentId: res.parent_id,
            parentReused: res.parent_reused ?? false,
            nodeIds: res.node_ids,
            count: res.node_ids.length,
            skipped: res.skipped_count ?? 0,
          }),
      },
    );
  };

  const undo = () => {
    if (!done) return;
    setUndoFailed(false);
    const options = {
      onSuccess: () => setUndone(true),
      onError: () => setUndoFailed(true),
    };
    if (!done.parentReused) {
      archive.mutate({ nodeId: done.parentId, cascade: true }, options);
      return;
    }
    // The day node was there before this paste: remove only the tasks it made.
    archiveMany.mutate({ nodeIds: done.nodeIds }, options);
  };

  return (
    <SideDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Paste a run sheet"
      description="Tarii reads the time, duty, place and name on each line."
      width="lg"
      testid="run-sheet-panel"
    >
      <div className="max-w-full space-y-3 p-3">
        {done ? (
          <div data-testid="run-sheet-done" className="space-y-3">
            <p className="text-sm text-slate-800">
              {undone
                ? "The tasks are removed."
                : `Made ${done.count} ${done.count === 1 ? "task" : "tasks"}.${
                    done.skipped > 0
                      ? ` ${done.skipped} ${done.skipped === 1 ? "line" : "lines"} had no start time and ${done.skipped === 1 ? "was" : "were"} skipped.`
                      : ""
                  }`}
            </p>
            {!undone && (
              <button
                type="button"
                data-testid="run-sheet-undo"
                onClick={undo}
                disabled={archive.isPending || archiveMany.isPending}
                className={BUTTON}
              >
                Undo
              </button>
            )}
            {undoFailed && !undone && (
              <p role="alert" className="text-sm text-red-700">
                Undo did not finish. Try again.
              </p>
            )}
          </div>
        ) : (
          <>
            <label className="block text-sm text-slate-700">
              Day
              <input
                type="date"
                data-testid="run-sheet-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`${INPUT} mt-1`}
              />
            </label>
            <label className="block text-sm text-slate-700">
              Run sheet
              <textarea
                data-testid="run-sheet-text"
                value={text}
                rows={8}
                onChange={(e) => {
                  setText(e.target.value);
                  // The rows no longer match the text: read it again.
                  setRows(null);
                }}
                placeholder="One duty per line: 17:30 Place table numbers, Hall B - Mei"
                className={`${INPUT} mt-1 font-mono`}
              />
            </label>
            <button
              type="button"
              data-testid="run-sheet-preview"
              onClick={runPreview}
              disabled={text.trim().length === 0 || !date || preview.isPending}
              className={BUTTON}
            >
              Preview
            </button>
            {preview.isError && (
              <p role="alert" className="text-sm text-red-700">
                We could not read the run sheet. Check the lines and try again.
              </p>
            )}
            {rows && rows.length === 0 && (
              <p data-testid="run-sheet-empty" className="text-sm text-slate-600">
                No lines to read. Paste one duty per line.
              </p>
            )}
            {rows && rows.length > 0 && (
              <div data-testid="run-sheet-rows" className="space-y-2">
                {rows.map((row, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id and keep their order
                    key={i}
                    data-testid="run-sheet-row"
                    className="grid grid-cols-[9rem_1fr] gap-2 rounded border border-slate-200 p-2"
                  >
                    <input
                      type="time"
                      aria-label="Time"
                      data-testid="run-sheet-row-time"
                      value={row.time ?? ""}
                      onChange={(e) => edit(i, { time: e.target.value || null })}
                      className={INPUT}
                    />
                    <input
                      type="text"
                      aria-label="Duty"
                      data-testid="run-sheet-row-title"
                      value={row.title}
                      onChange={(e) => edit(i, { title: e.target.value })}
                      className={INPUT}
                    />
                    <input
                      type="text"
                      aria-label="Place"
                      data-testid="run-sheet-row-place"
                      value={row.place ?? ""}
                      onChange={(e) => edit(i, { place: e.target.value || null })}
                      className={INPUT}
                    />
                    <select
                      aria-label="Holder"
                      data-testid="run-sheet-row-holder"
                      value={row.holderMembershipId ?? ""}
                      onChange={(e) =>
                        edit(i, {
                          holderMembershipId: e.target.value || null,
                          holderName: null,
                          warnings: row.warnings.filter((w) => !w.includes("member called")),
                        })
                      }
                      className={INPUT}
                    >
                      <option value="">No holder</option>
                      {members.map((m) => (
                        <option key={m.membershipId} value={m.membershipId}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {(row.warnings.length > 0 || !row.title.trim()) && (
                      <ul
                        data-testid="run-sheet-row-warnings"
                        className="col-span-full text-xs text-amber-800"
                      >
                        {row.warnings
                          .filter((w) => !(w === "No start time" && row.time))
                          .map((w) => (
                            <li key={w} className={w === "No start time" ? "text-red-700" : ""}>
                              {w}
                            </li>
                          ))}
                        {!row.title.trim() && <li>Add a duty name or this line is skipped.</li>}
                      </ul>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  data-testid="run-sheet-make"
                  onClick={make}
                  disabled={timed.length === 0 || create.isPending || rowError !== null}
                  className={PRIMARY}
                  style={{ backgroundColor: "#3F8B8B" }}
                >
                  {`Make ${timed.length} ${timed.length === 1 ? "task" : "tasks"}`}
                </button>
                {untimed > 0 && (
                  <p data-testid="run-sheet-untimed" className="text-sm text-red-700">
                    {`${untimed} ${untimed === 1 ? "line has" : "lines have"} no start time and will be skipped.`}
                  </p>
                )}
                {rowError && (
                  <p role="alert" className="text-sm text-red-700">
                    {rowError}
                  </p>
                )}
                {create.isError && (
                  <p role="alert" className="text-sm text-red-700">
                    We could not make the tasks. Nothing was saved. Try again.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </SideDrawer>
  );
}
